import React, { useContext, useEffect, useState } from "react";
import { Button as ButtonBase } from "@mui/material";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import PageLayout from "@/components/layouts/page/PageLayout";
import NoDataFound from "@/components/NoDataFound";
import { BRAND_CONST, HTTP_STATUS } from "@/config/constants";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { logger } from "@/helpers/loggerHelper";
import { getDashboardPath } from "@/helpers/utils";
import { nationalPlusUpCampaignService } from "../services/nationalPlusUpCampaignService";
import { isNationalPlusUpCampaign } from "../utils/nationalPlusUpHelpers";
import NationalPlusUpJoinCampaign from "../components/NationalPlusUpJoinCampaign";

type NationalPlusUpRecord = Record<string, any>;

type GlobalContextValue = {
  loggedInUser?: NationalPlusUpRecord;
  selectedBrand?: NationalPlusUpRecord;
};

type NationalPlusUpCampaignResponse = {
  status?: number;
  data?: NationalPlusUpRecord & {
    rows?: NationalPlusUpRecord[];
  };
};

const Button = ButtonBase as React.ComponentType<Record<string, any>>;

function NationalPlusUpJoinCampaignPage() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalContextValue;
  const loader = useContext(LoaderContext) as (_loading: boolean) => void;
  const [campaign, setCampaign] = useState<NationalPlusUpRecord | null>(null);
  const [loading, setLoading] = useState(false);

  const participantId =
    searchParams.get("participantId") || loggedInUser?.participantData?.id;

  useEffect(() => {
    async function fetchCampaign() {
      setLoading(true);
      try {
        const res =
          await nationalPlusUpCampaignService.getNationalPlusUpCampaign({
            id: campaignId,
            brand_id: selectedBrand?.id,
            fk_participant_id: participantId,
          });
        const response = res as NationalPlusUpCampaignResponse;
        if (response.status === HTTP_STATUS.OK) {
          const campaignData = response.data?.rows?.[0] || response.data;
          if (campaignData?.campaign_advanced_settings?.[0]) {
            campaignData.campaign_advanced_setting =
              campaignData.campaign_advanced_settings[0];
          }
          setCampaign(campaignData || null);
        }
      } catch (err) {
        logger.error("Fetch National Plus Up campaign error", err);
      } finally {
        setLoading(false);
      }
    }

    if (campaignId && selectedBrand?.id && participantId) {
      fetchCampaign();
    }
  }, [campaignId, selectedBrand?.id, participantId]);

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  if (
    selectedBrand?.id === BRAND_CONST.MS_BRAND_ID ||
    selectedBrand?.id === BRAND_CONST.KUMON_BRAND_ID
  ) {
    navigate(`${getDashboardPath()}`, { replace: true });
    return null;
  }

  const renderContent = () => {
    if (loading) return <NoDataFound message="Loading..." />;
    if (!campaign) return <NoDataFound message="No campaign found!" />;
    if (!isNationalPlusUpCampaign(campaign)) {
      return (
        <NoDataFound
          message={
            <div className="d-flex-col-center">
              <div>
                This campaign is not available as a National Plus Up flow.
              </div>
              <Button
                component={Link}
                to={`${getDashboardPath()}`}
                variant="contained"
                color="primary"
                className="my-4"
                size="large"
              >
                Back to dashboard
              </Button>
            </div>
          }
        />
      );
    }

    return <NationalPlusUpJoinCampaign campaign={campaign} />;
  };

  return (
    <PageLayout className="join-campaign-page postcard-container">
      {renderContent()}
    </PageLayout>
  );
}

export default NationalPlusUpJoinCampaignPage;
